import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Slot, { type SlotBarPosition } from './Slot';
import TimeSignatureSlot from './TimeSignatureSlot';
import BarEditorModal from './BarEditorModal';
import { BarState, RowState, ChordObjectModelV3, TimeSignature } from '../types';
import { DEFAULT_TIME_SIGNATURE, KeySignature, getRowSlots, layoutBarSlots } from '@chordbaker/music-theory';
import useRowScroll from '../hooks/useRowScroll';
import { useTheme, Theme } from '../contexts/ThemeContext';

// RN-ALIGNED: Clean createStyles function matching React Native pattern
const createStyles = (theme: Theme) => ({
  rowContainer: {
    marginBottom: 0,
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
    borderRadius: theme.radii.md,
    backgroundColor: 'transparent',
  },
  rowFlexWrapper: {
    width: '100%',
    maxWidth: '80rem', // 5xl equivalent — same as previous scrollContainer max
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'stretch',
  },
  scrollAndFadeWrapper: {
    position: 'relative' as const,
    flex: 1,
    minWidth: 0, // required so flex child can shrink and overflow-x: auto works
  },
  scrollContainer: {
    width: '100%',
    overflowX: 'auto' as const,
    overflowY: 'hidden' as const,
    position: 'relative' as const,
  },
  slotsContainer: {
    display: 'flex',
    width: 'fit-content',
    minWidth: '100%',
  },
  leftGradient: {
    position: 'absolute' as const,
    left: 0,
    top: 0,
    bottom: 0,
    width: 48, // 12 * 4px = 48px
    pointerEvents: 'none' as const,
    zIndex: 10,
  },
  rightGradient: {
    position: 'absolute' as const,
    right: 0,
    top: 0,
    bottom: 0,
    width: 48, // 12 * 4px = 48px  
    pointerEvents: 'none' as const,
    zIndex: 10,
  },
  actionsContainer: {
    width: '100%',
    maxWidth: '80rem', // 5xl equivalent
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: theme.spacing.xs,
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'flex-end',
    gap: theme.spacing.xxs,
    paddingRight: theme.spacing.xs,
  },
  // Flat text buttons (same fill as the section buttons, no border) so the row
  // chrome stays quieter than the chart content. Delete is the one that carries color.
  actionButton: {
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: theme.radii.sm,
    height: theme.sizes.compactButton,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.typography.sizes.sm,
    fontWeight: theme.typography.weights.medium,
    color: theme.colors.textSecondary,
    backgroundColor: theme.colors.surfaceSecondary,
    border: 'none',
    cursor: 'pointer',
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
    touchAction: 'manipulation',
  },
  actionButtonError: {
    color: theme.colors.error,
  },
  actionButtonDisabled: {
    opacity: theme.opacity.disabled,
    cursor: 'not-allowed',
  },
  buttonPlaceholder: {
    height: theme.sizes.compactButton, // same as action buttons
    width: '100%',
  },
  // WEB-SPECIFIC: Responsive text sizes
  actionButtonTextSmall: {
    fontSize: theme.typography.sizes.xs,
  },
  actionButtonTextNormal: {
    fontSize: theme.typography.sizes.sm,
  },
});

export interface RowProps {
  row: RowState;
  sectionId: string; // Track which section this row belongs to
  onAddSlot: (rowId: string, afterSlotId: string) => void;
  onAddSlotBefore: (rowId: string, beforeSlotId: string) => void;
  onDeleteSlot: (rowId: string, slotId: string) => void;
  onUpdateChord: (rowId: string, slotId: string, newChordData: ChordObjectModelV3 | null, customText?: string) => void;
  onDeleteRow?: (rowId: string) => void;
  canDeleteRow?: boolean;
  showNotes?: boolean;
  showDegrees?: boolean;
  showLyrics?: boolean;
  selectedKey?: KeySignature | null;
  // Props for active slot state management
  activeSlotId?: string | null;
  onSlotPress?: (slotId: string) => void;
  onUpdateLyrics?: (rowId: string, slotId: string, lyrics: string) => void;
  // Props for active row state management (from centralized management)
  activeRowId?: string | null;
  onRowPress?: (rowId: string) => void;
  onClearActiveRow?: () => void;
  onClearActiveSlot?: () => void;
  // Centralized control visibility function
  getControlVisibility?: (elementId: string, elementType: 'section' | 'row' | 'slot') => boolean;
  onAddRowAfter?: (currentRowId: string) => void;
  onDuplicateRow?: (rowIdToDuplicate: string) => void;
  onAddSectionAfterRow?: (currentRowId: string) => void;
  onSetRowTimeSignature?: (rowId: string, timeSignature: TimeSignature | null) => void;
  /** The song has a global time signature: rows show none and offer no "add". */
  hasGlobalTimeSignature?: boolean;
  /** The meter the row plays in (`getRowMeters`): bar layout, dots and the bar editor. */
  meter?: TimeSignature;
  /** Beats is on and the song has a meter: dots under every slot, beats button in the pill. */
  showBeats?: boolean;
  /** The bar editor's Apply: the edited bar replaces the original; `focusSlotId` is the slot to leave active. */
  onApplyBar?: (rowId: string, barId: string, bar: BarState, focusSlotId: string) => void;
  /** + before/after on an inner slot of a split bar. */
  onCantAddInside?: (rowId: string, slotId: string) => void;
  /** Set when the song has no time signature (the chord editor's Beats shortcut asks for one). */
  onRequestSetTimeSignature?: () => void;
  /** A just-added slot whose chord editor should open, and the reset after it does. */
  slotToOpenChordEditor?: string | null;
  onChordEditorOpened?: () => void;
}

const Row: React.FC<RowProps> = ({
  row,
  sectionId,
  onAddSlot,
  onAddSlotBefore,
  onDeleteSlot,
  onUpdateChord,
  onDeleteRow,
  canDeleteRow = true,
  showNotes = false,
  showDegrees = false,
  showLyrics = false,
  selectedKey = null,
  activeSlotId,
  onSlotPress,
  onUpdateLyrics,
  activeRowId,
  onRowPress,
  onClearActiveRow,
  onClearActiveSlot,
  getControlVisibility,
  onAddRowAfter,
  onDuplicateRow,
  onAddSectionAfterRow,
  onSetRowTimeSignature,
  hasGlobalTimeSignature = false,
  meter = DEFAULT_TIME_SIGNATURE,
  showBeats = false,
  onApplyBar,
  onCantAddInside,
  onRequestSetTimeSignature,
  slotToOpenChordEditor,
  onChordEditorOpened,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [isRowHovered, setIsRowHovered] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // The row's slots in playing order, across its bars
  const slots = useMemo(() => getRowSlots(row), [row]);

  // Each slot's place in its bar: bar edges (dividers, + before/after) and beat layout
  const barPositions = useMemo(() => {
    const positions = new Map<string, SlotBarPosition>();
    for (const bar of row.bars) {
      layoutBarSlots(bar, meter).forEach((layout, i) => {
        positions.set(layout.slotId, { isFirstInBar: i === 0, isLastInBar: layout.isLast, layout });
      });
    }
    return positions;
  }, [row, meter]);

  // The slot whose bar editor is open (owned here: it needs the whole row)
  const [barEditorSlotId, setBarEditorSlotId] = useState<string | null>(null);

  // Use the custom scroll hook for scroll detection
  const {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    updateScrollability,
  } = useRowScroll({
    containerWidth,
    slotsLength: slots.length,
  });

  // WEB-SPECIFIC: Handle scrollbar visibility like React Native
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    updateScrollability();

    // Show scrollbar when actively scrolling
    setIsScrolling(true);

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Hide scrollbar after 1 second of no scrolling (like React Native)
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  };

  // The row sits inside the section's own click area: letting the click
  // bubble there would toggle the section and clear the row straight away
  const handleRowPress = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    if (onRowPress) {
      onRowPress(row.id);
    }
  };

  const handleSlotPress = (slotId: string) => {
    if (onSlotPress) {
      onSlotPress(slotId);
    }
  };

  const handleContainerResize = useCallback(() => {
    if (scrollContainerRef.current) {
      setContainerWidth(scrollContainerRef.current.clientWidth);
    }
    // Update screen size for responsive behavior
    setIsLargeScreen(window.innerWidth >= 640);
  }, [scrollContainerRef]);

  useEffect(() => {
    handleContainerResize();
    window.addEventListener('resize', handleContainerResize);
    return () => {
      window.removeEventListener('resize', handleContainerResize);
      // Cleanup scroll timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleContainerResize]);

  // WEB-SPECIFIC: Show scrollbar briefly when content becomes scrollable (like React Native)
  useEffect(() => {
    if (canScrollLeft || canScrollRight) {
      setIsScrolling(true);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1500); // Show slightly longer on initial scroll capability
    }
  }, [canScrollLeft, canScrollRight]);

  const isActive = getControlVisibility ? getControlVisibility(row.id, 'row') : false;

  return (
    <div
      style={styles.rowContainer}
      onMouseEnter={() => setIsRowHovered(true)}
      onMouseLeave={() => setIsRowHovered(false)}
      onClick={handleRowPress}
      role="button"
      tabIndex={0}
      data-testid={`chart-row-${row.id}`}
      onKeyDown={(e) => {
        // Don't intercept keyboard events from input, textarea, or contenteditable elements
        const target = e.target as HTMLElement;
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        ) {
          return;
        }

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRowPress(e);
        }
      }}
    >
      {/* Scrollable chord area with fade effects */}
      <div style={styles.rowFlexWrapper}>
        {!hasGlobalTimeSignature && (
          <TimeSignatureSlot
            timeSignature={row.timeSignature ?? null}
            isRowActive={isActive}
            onChange={(value) => {
              if (onSetRowTimeSignature) {
                onSetRowTimeSignature(row.id, value);
              }
            }}
          />
        )}
        <div style={styles.scrollAndFadeWrapper}>
          {/* WEB-SPECIFIC: Scrollbar visibility differs from React Native by design:
              - RN: Hidden by default, shows only when actively scrolling (touch-based)
              - Web: Also shows on hover for discoverability (mouse-based interaction)
              This provides better UX for desktop users who need visual cues for scrollable content */}
          <div
            ref={scrollContainerRef}
            className={`custom-scrollbar ${isScrolling || isRowHovered ? 'scrollbar-visible' : 'scrollbar-hidden'
              }`}
            style={styles.scrollContainer}
            onScroll={handleScroll}
          >
            {/* WEB-SPECIFIC: Container with natural width (RN-aligned approach) */}
            <div style={styles.slotsContainer}>
              {slots.map((slotState, index) => (
                <Slot
                  key={slotState.id}
                  slot={slotState}
                  barPosition={barPositions.get(slotState.id)}
                  meter={meter}
                  showBeats={showBeats}
                  onOpenBeatsEditor={setBarEditorSlotId}
                  onRequestSetTimeSignature={onRequestSetTimeSignature}
                  shouldOpenChordEditor={slotToOpenChordEditor === slotState.id}
                  onChordEditorOpened={onChordEditorOpened}
                  onCantAddInside={(slotId) => onCantAddInside?.(row.id, slotId)}
                  onAddSlot={(afterSlotId) => {
                    onAddSlot(row.id, afterSlotId);
                  }}
                  onAddSlotBefore={(beforeSlotId) => {
                    onAddSlotBefore(row.id, beforeSlotId);
                  }}
                  onDeleteSlot={(slotIdToDelete) => {
                    onDeleteSlot(row.id, slotIdToDelete);
                  }}
                  onUpdateChord={(slotId, newChord, customText) => {
                    onUpdateChord(row.id, slotId, newChord, customText);
                  }}
                  onUpdateLyrics={(slotId, lyrics) => {
                    if (onUpdateLyrics) {
                      onUpdateLyrics(row.id, slotId, lyrics);
                    }
                  }}
                  isLast={index === slots.length - 1}
                  canDelete={slots.length > 1}
                  showNotes={showNotes}
                  showDegrees={showDegrees}
                  showLyrics={showLyrics}
                  selectedKey={selectedKey}
                  activeSlotId={activeSlotId}
                  onSlotPress={handleSlotPress}
                />
              ))}
            </div>
          </div>

          {/* WEB-SPECIFIC: Fade gradients positioned over the scroll container like RN LinearGradients
              Using React elements instead of CSS pseudo-elements ensures proper positioning
              and theme integration. This matches RN's approach of absolutely positioned LinearGradients. */}
          {canScrollLeft && (
            <div
              style={{
                ...styles.leftGradient,
                background: `linear-gradient(to right, ${theme.colors.background}, transparent)`
              }}
            />
          )}
          {canScrollRight && (
            <div
              style={{
                ...styles.rightGradient,
                background: `linear-gradient(to left, ${theme.colors.background}, transparent)`
              }}
            />
          )}
        </div>
      </div>

      {barEditorSlotId && (
        <BarEditorModal
          isOpen
          row={row}
          slotId={barEditorSlotId}
          meter={meter}
          onClose={() => setBarEditorSlotId(null)}
          onApply={(barId, bar, focusSlotId) => {
            onApplyBar?.(row.id, barId, bar, focusSlotId);
            setBarEditorSlotId(null);
          }}
        />
      )}

      {/* Action buttons - show when row is active */}
      <div style={styles.actionsContainer}>
        {isActive ? (
          <>
            {/* DON'T REMOVE!!!! */}
            {/* <button
              onClick={(e) => {
                e.stopPropagation();
                onAddSectionAfterRow(rowData.id);
              }}
              style={{
                ...styles.actionButton,
                ...styles.actionButton,
                ...(isLargeScreen ? styles.actionButtonTextNormal : styles.actionButtonTextSmall),
              }}
            >
              Add Section
            </button> */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onAddRowAfter) {
                  onAddRowAfter(row.id);
                }
              }}
              style={{
                ...styles.actionButton,
                ...(isLargeScreen ? styles.actionButtonTextNormal : styles.actionButtonTextSmall),
              }}
            >
              Add Row
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onDuplicateRow) {
                  onDuplicateRow(row.id);
                }
              }}
              style={{
                ...styles.actionButton,
                ...(isLargeScreen ? styles.actionButtonTextNormal : styles.actionButtonTextSmall),
              }}
            >
              Duplicate Row
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onDeleteRow) {
                  onDeleteRow(row.id);
                }
              }}
              style={{
                ...styles.actionButton,
                ...(!canDeleteRow ? styles.actionButtonDisabled : styles.actionButtonError),
                ...(isLargeScreen ? styles.actionButtonTextNormal : styles.actionButtonTextSmall),
              }}
              disabled={!canDeleteRow}
            >
              Delete Row
            </button>
          </>
        ) : (
          // Empty placeholder with same height to maintain consistent spacing
          <div style={styles.buttonPlaceholder} />
        )}
      </div>
    </div>
  );
};

export default Row;
